import { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/table";
import { Heading } from "../components/heading";
import { Button } from "../components/button";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  setDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { formatCurrency } from "../helpers/currency.helpers";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "../components/dialog";
import { Fieldset, Label } from "../components/fieldset";
import * as Headless from "@headlessui/react";
import { Input } from "../components/input";
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownMenu,
} from "../components/dropdown";
import { EllipsisHorizontalIcon, PlusIcon } from "@heroicons/react/20/solid";
import EmptyState from "./components/empty-state";
import { BSGroom, GroomLocation } from "../models/groom";
import { formatDate, formatDateToInputString } from "../helpers/date.helpers";
import { Select } from "../components/select";
import { useTableSort } from '../hooks/useTableSort';
import { useColumnVisibility, ColumnConfig } from '../hooks/useColumnVisibility';
import { useTableSearch } from '../hooks/useTableSearch';
import { ChevronUpIcon, ChevronDownIcon, ChevronUpDownIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import { Switch, SwitchField } from "../components/switch";
import { InlineDatePicker } from "./components/inline-date-picker";

export default function Grooms() {
  const collectionName = "grooms";
  const emptyObject: BSGroom = {
    id: "",
    name: "",
    startDate: null,
    endDate: null,
    weeklySalary: 0,
    location: "florida",
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Filtering
  let [activeEmployee, setActiveEmployee] = useState(true)

  const [newObject, setNewObject] = useState<BSGroom>(emptyObject);
  const [objects, setObjects] = useState<BSGroom[]>([]);

  useEffect(() => {
    const fetchData = () => {
      const q = query(collection(db, collectionName));
      onSnapshot(q, (querySnapshot) => {
        const objectsArray: BSGroom[] = [];
        querySnapshot.forEach((doc) => {
            const serviceData = doc.data() as BSGroom;
            const startDate = serviceData.startDate instanceof Timestamp
              ? serviceData.startDate.toDate() 
              : serviceData.startDate;
  
            const endDate = serviceData.endDate instanceof Timestamp 
              ? serviceData.endDate.toDate() 
              : serviceData.endDate;
  
            objectsArray.push({ ...serviceData, id: doc.id, startDate, endDate });
        });
        setObjects(objectsArray);
      });
    };

    fetchData();
  }, []);

  const saveObject = async () => {
    if (newObject.id === "") {
      const newDocRef = doc(collection(db, collectionName));
      newObject.id = newDocRef.id;
      await setDoc(newDocRef, newObject);
    } else {
      await updateDoc(doc(db, collectionName, newObject.id), newObject);
    }

    setIsDialogOpen(false);
    resetForm();
  };

  const editObject = (object: BSGroom) => {
    setNewObject(object);
    setIsDialogOpen(true);
  };

  const deleteObject = async (object: BSGroom) => {
    await deleteDoc(doc(db, collectionName, object.id));
  };

  const resetForm = () => {
    setNewObject(emptyObject);
  };

  // Add columns configuration
  const columns: ColumnConfig<BSGroom>[] = [
    { field: 'name', label: 'Name', defaultVisible: true },
    { field: 'startDate', label: 'Start Date', defaultVisible: true },
    { field: 'endDate', label: 'End Date', defaultVisible: true },
    { field: 'weeklySalary', label: 'Weekly Salary', defaultVisible: true },
    { field: 'location', label: 'Location', defaultVisible: true },
    { field: 'actions', label: 'Actions', defaultVisible: true }
  ];

  // Add hooks
  const { sortField, sortDirection, handleSort, getSortedData } = useTableSort<BSGroom>('name');
  const { toggleColumn, isColumnVisible } = useColumnVisibility<BSGroom>(columns);
  const { searchQuery, setSearchQuery, getFilteredData } = useTableSearch<BSGroom>();

  // Add SortableHeader component
  const SortableHeader = ({ field, children }: { field: keyof BSGroom, children: React.ReactNode }) => {
    const isCurrent = sortField === field;
    return (
      <TableHeader 
        className="cursor-pointer select-none group hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <span className="inline-flex">
            {isCurrent ? (
              sortDirection === 'asc' ? (
                <ChevronUpIcon className="w-4 h-4" />
              ) : (
                <ChevronDownIcon className="w-4 h-4" />
              )
            ) : (
              <ChevronUpDownIcon className="w-4 h-4 opacity-0 group-hover:opacity-50" />
            )}
          </span>
        </div>
      </TableHeader>
    );
  };

  // Add filtered data function
  const getFilteredGrooms = () => {
    let filtered = objects;

    // Filter by in-property status
    if (activeEmployee) {
      const currentDate = new Date();
      filtered = filtered.filter(groom => {
        const isAfterCheckIn = !groom.startDate || groom.startDate <= currentDate;
        const isBeforeCheckOut = !groom.endDate || groom.endDate >= currentDate;
        return isAfterCheckIn && isBeforeCheckOut;
      });
    }

    filtered = getFilteredData(filtered, (groom) => [
      groom.name || '',
      formatDate(groom.startDate) || '',
      formatDate(groom.endDate) || '',
      formatCurrency(groom.weeklySalary),
      groom.location || ''
    ]);

    return getSortedData(filtered);
  };

  const updateStartDate = async (groom: BSGroom, date: Date | null) => {
    try {
      await updateDoc(doc(db, 'grooms', groom.id), {
        startDate: date
      });
    } catch (error) {
      console.error('Error updating start date:', error);
    }
  };

  const updateEndDate = async (groom: BSGroom, date: Date | null) => {
    try {
      await updateDoc(doc(db, 'grooms', groom.id), {
        endDate: date
      });
    } catch (error) {
      console.error('Error updating end date:', error);
    }
  };

  return (
    <div>
      <div className="flex w-full flex-wrap items-end justify-between gap-4 border-b border-zinc-950/10 pb-6 dark:border-white/10">
        <Heading>Grooms</Heading>
        <div className="flex items-center gap-4">
          <SwitchField>
            <Label>Showing {activeEmployee ? 'active employees' : 'all employees'}</Label>
            <Switch color="green" checked={activeEmployee} onChange={setActiveEmployee} />
          </SwitchField>
          <div className="relative">
            <Input
              type="search"
              placeholder="Search grooms..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-[250px]"
            />
          </div>
          <Dropdown>
            <DropdownButton>
              <EyeIcon className="w-5 h-5 mr-2" />
              Columns
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
              {columns.map(({ field, label }) => (
                <DropdownItem
                  key={field}
                  onClick={() => toggleColumn(field)}
                >
                  <div className="flex items-center gap-2">
                    {isColumnVisible(field) ? (
                      <EyeIcon className="w-4 h-4" />
                    ) : (
                      <EyeSlashIcon className="w-4 h-4" />
                    )}
                    {label}
                  </div>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
          >
            Add Groom
          </Button>
        </div>
      </div>

      {objects.length > 0 ? (
        <Table
          dense
          striped
          className="[--gutter:theme(spacing.6)] sm:[--gutter:theme(spacing.8)]"
        >
          <TableHead>
            <TableRow>
              {columns.map(({ field, label }) => 
                isColumnVisible(field) && (
                  field === 'actions' ? (
                    <TableHeader key={field}>Actions</TableHeader>
                  ) : (
                    <SortableHeader key={field} field={field}>
                      {label}
                    </SortableHeader>
                  )
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {getFilteredGrooms().map((object) => (
              <TableRow key={object.id}>
                {isColumnVisible('name') && (
                  <TableCell className="font-medium">{object.name}</TableCell>
                )}
                {isColumnVisible('startDate') && (
                  <TableCell>
                    <InlineDatePicker
                      value={object.startDate}
                      onChange={(date) => updateStartDate(object, date)}
                    />
                  </TableCell>
                )}
                {isColumnVisible('endDate') && (
                  <TableCell>
                    <InlineDatePicker
                      value={object.endDate}
                      onChange={(date) => updateEndDate(object, date)}
                    />
                  </TableCell>
                )}
                {isColumnVisible('weeklySalary') && (
                  <TableCell>{formatCurrency(object.weeklySalary)}</TableCell>
                )}
                {isColumnVisible('location') && (
                  <TableCell>{object.location === 'florida' ? 'Florida' : 'Long Island'}</TableCell>
                )}
                {isColumnVisible('actions') && (
                  <TableCell>
                    <div className="-mx-3 -my-1.5 sm:-mx-2.5">
                      <Dropdown>
                        <DropdownButton plain aria-label="More options">
                          <EllipsisHorizontalIcon />
                        </DropdownButton>
                        <DropdownMenu anchor="bottom end">
                          <DropdownItem onClick={() => editObject(object)}>
                            Edit
                          </DropdownItem>
                          <DropdownDivider />
                          <DropdownItem onClick={() => deleteObject(object)}>
                            Delete
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <EmptyState
          title={"No Grooms"}
          description={"Add new grooms to manage them here"}
        >
          <PlusIcon className="w-10 h-10" />
        </EmptyState>
      )}

      <Dialog open={isDialogOpen} onClose={setIsDialogOpen}>
        <DialogTitle>New Groom</DialogTitle>
        <DialogBody>
          <Fieldset>
            <div
              data-slot="control"
              className="grid grid-cols-1 items-center gap-x-4 gap-y-6 sm:grid-cols-3"
            >
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Name</Label>
                <Input
                  type="text"
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      name: e.target.value,
                    })
                  }
                  value={newObject.name}
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  name="name"
                />
              </Headless.Field>
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Start Date</Label>
                <input
                  type="date"
                  id="start"
                  name="start date"
                  value={formatDateToInputString(newObject.startDate)}
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      startDate: new Date(`${e.target.value}T00:00:00`),
                    })
                  }
                />
              </Headless.Field>
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>End Date</Label>
                <input
                  type="date"
                  id="end"
                  name="end date"
                  value={formatDateToInputString(newObject.endDate)}
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      endDate: new Date(`${e.target.value}T00:00:00`),
                    })
                  }
                />
              </Headless.Field>
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Weekly Salary</Label>
                <Input
                  type="number"
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      weeklySalary: e.target.value as any,
                    })
                  }
                  value={newObject.weeklySalary}
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  name="weekly salary"
                />
              </Headless.Field>
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Location</Label>
                <Select
                  onChange={(e) =>
                    setNewObject({
                      ...newObject,
                      location: e.target.value as GroomLocation,
                    })
                  }
                >
                  <option value="florida">Florida</option>
                  <option value="long-island">Long Island</option>
                </Select>
              </Headless.Field>
            </div>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setIsDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={() => saveObject()}>
            {newObject.id === "" ? "Add" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
